import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/providers";
import { BillingAPI } from "@/lib/api";
import Logo from "@/components/Logo";
import { Menu, X } from "lucide-react";

export default function NavBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [wallet, setWallet] = useState(null);

  // For external users only — keep a lightweight wallet poll so the credit
  // badge in the navbar stays in sync after an audit run or top-up. Skipped
  // for internal users to avoid an unnecessary API hit on every nav change.
  const isExternal = user?.user_type === "external" && user?.role !== "admin";
  useEffect(() => {
    if (!isExternal) { setWallet(null); return; }
    let cancelled = false;
    const fetchWallet = async () => {
      try {
        const { data } = await BillingAPI.wallet();
        if (!cancelled) setWallet(data.wallet);
      } catch (err) {
        // Nav badge is best-effort — a transient wallet-fetch failure
        // shouldn't noise the console or alarm the user. Keep this quiet
        // in prod but visible to anyone debugging navbar state.
        if (process.env.NODE_ENV !== "production") {
          console.warn("NavBar wallet fetch failed:", err?.message || err);
        }
      }
    };
    fetchWallet();
    // Re-fetch on every route change (audit run, top-up complete, etc.)
  }, [isExternal, location.pathname]);

  // Auto-close the drawer whenever the route changes
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll while the drawer is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const showAppLinks = user && user.status === "active";

  const onGoogleLogin = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    try {
      const o = encodeURIComponent(window.location.origin);
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login?origin=${o}`, { credentials: "include" });
      const j = await r.json();
      if (!j.auth_url) throw new Error("missing auth_url");
      window.location.href = j.auth_url;
    } catch {
      window.location.href = "/?auth_error=start_failed";
    }
  };

  const themeBtn = null; // Theme toggle removed — dark-only site.

  const userBlockDesktop = user ? (
    <>
      <span className="overline" data-testid="nav-user-name">{user.name?.split(" ")[0]}</span>
      <button
        className="v-btn-ghost v-btn"
        data-testid="nav-logout-btn"
        onClick={async () => { await logout(); navigate("/"); }}
        style={{ padding: "8px 14px", fontSize: 11 }}
      >
        Logout
      </button>
    </>
  ) : (
    <button
      type="button"
      data-testid="nav-login-btn"
      onClick={onGoogleLogin}
      className="v-btn"
      style={{ padding: "8px 18px", fontSize: 12, letterSpacing: ".06em" }}
    >
      Log in
    </button>
  );

  return (
    <header
      className="v-navbar"
      style={{
        position: "sticky", top: 0, zIndex: 50,
        backdropFilter: "blur(16px)",
        background: "color-mix(in srgb, var(--v-bg) 80%, transparent)",
        borderBottom: "1px solid var(--v-border)",
      }}
    >
      <div
        className="v-container"
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          height: 68, gap: 16,
        }}
      >
        <Link
          to={user ? "/studio" : "/"}
          data-testid="nav-logo"
          style={{ textDecoration: "none", color: "inherit", flexShrink: 0, minWidth: 0, maxWidth: "60%" }}
        >
          <Logo />
        </Link>

        {/* Desktop nav (hidden via CSS below the breakpoint) */}
        <nav className="v-nav-desktop" data-testid="nav-desktop">
          {showAppLinks && (
            <>
              <Link to="/studio" className="overline" data-testid="nav-studio">Studio</Link>
              <Link to="/projects/new" className="overline" data-testid="nav-new-project">New project</Link>
              {user.role === "admin" && (
                <Link to="/admin" className="overline" data-testid="nav-admin">Admin</Link>
              )}
              <Link to="/settings" className="overline" data-testid="nav-settings">Settings</Link>
            </>
          )}
          {/* Public links — always visible regardless of auth state. Designers
              and external users alike can hop to Pricing/About/Support without
              losing the app context. */}
          <Link to="/pricing" className="overline" data-testid="nav-pricing">Pricing</Link>
          <Link to="/framework" className="overline" data-testid="nav-framework">Framework</Link>
          <Link to="/about" className="overline" data-testid="nav-about">About</Link>
          <Link to="/support" className="overline" data-testid="nav-support">Support</Link>
          {isExternal && wallet && (
            <Link
              to="/pricing"
              data-testid="nav-credit-badge"
              title="Audit credits remaining — click to buy more"
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "6px 12px", borderRadius: 999,
                border: `1px solid ${wallet.audit_credits > 0 ? "var(--v-accent)" : "#ef4444"}`,
                background: wallet.audit_credits > 0 ? "rgba(6,182,212,.10)" : "rgba(239,68,68,.10)",
                color: "var(--v-text)", textDecoration: "none",
              }}
            >
              <span style={{
                width: 7, height: 7, borderRadius: "50%",
                background: wallet.audit_credits > 0 ? "#06B6D4" : "#ef4444",
              }} />
              <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".06em" }}>
                {wallet.audit_credits} {wallet.audit_credits === 1 ? "credit" : "credits"}
              </span>
              {wallet.audit_credits === 0 && (
                <span style={{ fontSize: 10, color: "var(--v-text-2)" }}>· Buy</span>
              )}
            </Link>
          )}
          {themeBtn}
          {userBlockDesktop}
        </nav>

        {/* Mobile cluster (hidden via CSS above the breakpoint) */}
        <div className="v-nav-mobile">
          {themeBtn}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-hamburger-btn"
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 42, height: 42,
              background: "transparent", border: "1px solid var(--v-border)",
              color: "var(--v-text)", cursor: "pointer", borderRadius: 6,
              flexShrink: 0,
            }}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer — only ever shown via the .v-nav-mobile parent CSS */}
      {open && (
        <div
          data-testid="nav-mobile-drawer"
          className="v-nav-drawer"
        >
          <div
            className="v-container"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              padding: "16px 0 24px",
            }}
          >
            {showAppLinks ? (
              <>
                <MobileLink to="/studio" testId="m-nav-studio">Studio</MobileLink>
                <MobileLink to="/projects/new" testId="m-nav-new-project">New project</MobileLink>
                {user.role === "admin" && <MobileLink to="/admin" testId="m-nav-admin">Admin</MobileLink>}
                <MobileLink to="/settings" testId="m-nav-settings">Settings</MobileLink>
                <div style={{ height: 1, background: "var(--v-border)", margin: "10px 0" }} />
              </>
            ) : null}
            <MobileLink to="/pricing" testId="m-nav-pricing">Pricing</MobileLink>
            <MobileLink to="/framework" testId="m-nav-framework">Framework</MobileLink>
            <MobileLink to="/faq" testId="m-nav-faq">FAQ</MobileLink>
            <MobileLink to="/about" testId="m-nav-about">About</MobileLink>
            <MobileLink to="/support" testId="m-nav-support">Support</MobileLink>

            {/* Credit chip (mobile) — same role as the navbar chip but
                vertically laid out inside the drawer so paying users can see
                their remaining credits at a glance on phones. */}
            {isExternal && wallet && (
              <Link
                to="/pricing"
                data-testid="m-nav-credit-badge"
                onClick={() => setOpen(false)}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  marginTop: 10, padding: "12px 14px", borderRadius: 10,
                  border: `1px solid ${wallet.audit_credits > 0 ? "var(--v-accent)" : "#ef4444"}`,
                  background: wallet.audit_credits > 0 ? "rgba(6,182,212,.08)" : "rgba(239,68,68,.08)",
                  textDecoration: "none", color: "inherit",
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: "50%",
                    background: wallet.audit_credits > 0 ? "#06B6D4" : "#ef4444",
                  }} />
                  <span style={{ fontSize: 14, fontWeight: 600 }}>
                    {wallet.audit_credits} audit {wallet.audit_credits === 1 ? "credit" : "credits"}
                  </span>
                </span>
                <span style={{ fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--v-text-2)" }}>
                  {wallet.audit_credits > 0 ? "Buy more →" : "Top up →"}
                </span>
              </Link>
            )}

            <div style={{ height: 1, background: "var(--v-border)", margin: "10px 0" }} />

            {user ? (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 2px", gap: 12, flexWrap: "wrap" }}>
                <div style={{ minWidth: 0 }}>
                  <div className="overline" style={{ color: "var(--v-text-2)" }}>Signed in as</div>
                  <div style={{ fontSize: 15, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis" }}>{user.name}</div>
                  <div className="mono" style={{ fontSize: 11, color: "var(--v-text-2)", wordBreak: "break-all" }}>{user.email}</div>
                </div>
                <button
                  className="v-btn v-btn-outline"
                  data-testid="m-nav-logout-btn"
                  onClick={async () => { await logout(); navigate("/"); }}
                  style={{ padding: "8px 14px", fontSize: 11, flexShrink: 0 }}
                >
                  Logout
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid="m-nav-login-btn"
                onClick={onGoogleLogin}
                className="v-btn"
                style={{ marginTop: 4 }}
              >
                Log in with Google →
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function MobileLink({ to, children, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      style={{
        display: "block",
        padding: "12px 2px",
        fontSize: 17,
        letterSpacing: "-.01em",
        textDecoration: "none",
        color: "inherit",
        borderBottom: "1px solid var(--v-border)",
      }}
    >
      {children}
    </Link>
  );
}
